/**
 * The geometry of a rendered PDF page: how big to paint it, and how to pin something to it.
 *
 * Everything here is arithmetic on numbers. There is no `document`, no `window`, no canvas and
 * no pdf.js — which is why it can be tested exhaustively rather than eyeballed in a browser,
 * and why the Worker can reuse `isHighlightAnchor` when highlights start being stored.
 *
 * Two jobs, and they meet at one number:
 *
 * 1. **`planPageRender`** decides three sizes that are easy to confuse and expensive to get
 *    wrong. The *CSS size* is how big the page is in layout — what a teacher sees and what
 *    the page scrolls by. The *backing store* is how many real pixels the canvas holds, which
 *    is the CSS size times the device pixel ratio; without that multiplier a retina screen
 *    shows a page upscaled from half resolution, which is the "blurry PDF" every viewer has
 *    once. The *render scale* is what pdf.js is asked for, and it targets the backing store.
 *
 * 2. **The anchor** — `{ page, rects, quote }` — is a selection expressed as fractions of the
 *    page box, so it survives every zoom level, every device pixel ratio and every window
 *    width. A rect captured on a phone at 390 CSS px maps to the right place on a 1300 px
 *    desktop column with no re-derivation, because the fraction of the page a sentence sits
 *    at is a fact about the document rather than about the screen it was read on.
 *
 * The number they share is the **page box**: the CSS-pixel rectangle the page is drawn into.
 * `planPageRender` computes it, the canvas and the text layer are both sized to it, and every
 * anchor is a fraction of it. One box, one source of truth, so alignment is arithmetic rather
 * than luck.
 */

/** A CSS-pixel rectangle, relative to the top-left of the page box. */
export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** The page box, in CSS pixels. */
export interface Box {
  readonly width: number;
  readonly height: number;
}

// --- what to paint, and how big ------------------------------------------------------------

export interface RenderPlanInput {
  /** The page at scale 1, in PDF units. */
  readonly pageWidth: number;
  readonly pageHeight: number;
  /** The CSS width the page may occupy at fit-width. */
  readonly containerWidth: number;
  /** 1 is fit-to-width. 1.5 is half as big again as that. */
  readonly zoom: number;
  /** `window.devicePixelRatio`. Passed in, because this module may not read a global. */
  readonly devicePixelRatio: number;
  /** Above this a page costs memory and buys nothing a human eye can see. */
  readonly maxDevicePixelRatio?: number;
  /** A ceiling on either side of the backing store, in device pixels. */
  readonly maxCanvasDimension?: number;
  /** A ceiling on the backing store's area. iOS is the binding constraint here. */
  readonly maxCanvasPixels?: number;
}

export interface RenderPlan {
  /** The page box: what the canvas, the text layer and every anchor are measured against. */
  readonly cssWidth: number;
  readonly cssHeight: number;
  /** `cssWidth / pageWidth`. The viewport scale the *text layer* is laid out at. */
  readonly cssScale: number;
  /** The canvas's `width`/`height` attributes, in device pixels. */
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** The viewport scale pdf.js is asked to paint at. Targets the backing store. */
  readonly renderScale: number;
  /** The device pixel ratio actually used, after the caps. */
  readonly dpr: number;
}

/**
 * Two is plenty.
 *
 * A 3x phone gains no visible sharpness over 2x on a document at reading size and costs 2.25x
 * the memory for every page it holds. Capping is the difference between a worksheet that opens
 * and a tab the system kills.
 */
export const MAX_DEVICE_PIXEL_RATIO = 2;

/**
 * Canvas ceilings, in device pixels.
 *
 * A canvas past either of these is not slow — it is *refused*, silently, and the page comes
 * back blank. 4096 on a side and ~16.7M pixels of area is iOS Safari's documented limit and
 * the lowest of any platform this runs on. Both are needed: a tall page at high zoom passes
 * the width test and fails the area one.
 */
export const MAX_CANVAS_DIMENSION = 4096;
export const MAX_CANVAS_PIXELS = 16_000_000;

const atLeastOnePixel = (n: number): number => (Number.isFinite(n) && n > 1 ? n : 1);
const wholePixels = (n: number): number => (Number.isFinite(n) && n > 1 ? Math.floor(n) : 1);

/**
 * The three sizes, derived once so nothing downstream has to re-derive them.
 *
 * The page box is deliberately **not** rounded to a whole pixel, and `cssScale` is
 * `cssWidth / pageWidth` exactly. pdf.js's text layer sizes its container with
 * `round(down, var(--total-scale-factor) * <pageWidth>px, var(--scale-round-x))`, so rounding
 * here would mean two independent roundings of the same quantity agreeing — which they do
 * not, once floating point is involved: a page 3899 px wide came back as a text layer 3898 px
 * wide, and a text layer a pixel short is a highlight a pixel out at the right-hand edge.
 * Left unrounded, with `--scale-round-x` set to a hundredth of a pixel, the two agree to
 * within a hundredth of a pixel by arithmetic rather than by coincidence.
 *
 * Only the canvas is whole, because a backing store has to be.
 */
export function planPageRender(input: RenderPlanInput): RenderPlan {
  const {
    pageWidth, pageHeight, containerWidth, zoom, devicePixelRatio,
    maxDevicePixelRatio = MAX_DEVICE_PIXEL_RATIO,
    maxCanvasDimension = MAX_CANVAS_DIMENSION,
    maxCanvasPixels = MAX_CANVAS_PIXELS,
  } = input;

  // A page with no width is a document we cannot lay out. One pixel, and the caller's
  // `unreadable` path takes it from there rather than a NaN spreading through the styles.
  if (!(pageWidth > 0) || !(pageHeight > 0)) {
    return {
      cssWidth: 1, cssHeight: 1, cssScale: 1,
      canvasWidth: 1, canvasHeight: 1, renderScale: 1, dpr: 1,
    };
  }

  const cssWidth = atLeastOnePixel(Math.max(1, containerWidth) * Math.max(0.05, zoom));
  const cssScale = cssWidth / pageWidth;
  const cssHeight = atLeastOnePixel(cssScale * pageHeight);

  const asked = Math.max(1, Math.min(devicePixelRatio || 1, maxDevicePixelRatio));
  const byWidth = maxCanvasDimension / (cssWidth * asked);
  const byHeight = maxCanvasDimension / (cssHeight * asked);
  const byArea = Math.sqrt(maxCanvasPixels / (cssWidth * cssHeight * asked * asked));
  // Never above what was asked for — the caps only ever take away.
  const dpr = asked * Math.min(1, byWidth, byHeight, byArea);

  const canvasWidth = wholePixels(cssWidth * dpr);
  const canvasHeight = wholePixels(cssHeight * dpr);

  return {
    cssWidth,
    cssHeight,
    cssScale,
    canvasWidth,
    canvasHeight,
    // Derived from the backing store rather than from `dpr`, so the viewport pdf.js paints
    // into is the canvas's own width and not a number that rounds near it.
    renderScale: canvasWidth / pageWidth,
    dpr,
  };
}

// --- a document of pages, one under another --------------------------------------------------

/**
 * The gap between two pages, in CSS pixels.
 *
 * It lives here rather than only in the stylesheet because every offset below counts it: the
 * scroll position of page seven is six gaps plus six page heights, and a stylesheet that said
 * 16 while this said 12 would put the page indicator a page out by the bottom of a long
 * document. The viewer sets the gap from this constant, so the two agree by construction.
 */
export const PAGE_GAP = 12;

/**
 * How many pages either side of the visible one hold a painted canvas.
 *
 * This is the number that makes continuous scroll affordable, and the figures are measured
 * rather than estimated. A letter page at fit-width in a 900 px column, at a device pixel
 * ratio of 2, is a 1,792 x 2,319 backing store — 16.6 MB. A forty-page unit plan held live is
 * therefore about 660 MB and a tab the system kills, which is the whole reason the pager
 * existed. One page either side is at most three live canvases: ~50 MB on a laptop and ~4.4 MB
 * in a phone's column, whatever the document's length.
 */
export const RENDER_WINDOW_RADIUS = 1;

/** A page at scale 1, in PDF units. What `getViewport({ scale: 1 })` reports. */
export interface PageSize {
  readonly width: number;
  readonly height: number;
}

export type DocumentPlanInput = Omit<RenderPlanInput, 'pageWidth' | 'pageHeight'>;

/**
 * Every page's box, planned at once.
 *
 * Sizing a page does not require painting it — `getViewport` answers from the page's
 * dictionary — so the scroll height is right from the first frame and the scrollbar does not
 * jump as pages paint. That is the property this function exists for: a document whose boxes
 * are planned lazily is a document whose scrollbar walks backwards under the teacher's thumb.
 */
export function planDocument(
  sizes: readonly PageSize[],
  input: DocumentPlanInput,
): RenderPlan[] {
  return sizes.map((size) =>
    planPageRender({ ...input, pageWidth: size.width, pageHeight: size.height }));
}

/** The top of each page in the scrolling column, in CSS pixels. */
export function pageOffsets(plans: readonly RenderPlan[], gap: number = PAGE_GAP): number[] {
  const offsets: number[] = [];
  let top = 0;
  for (const plan of plans) {
    offsets.push(top);
    top += plan.cssHeight + gap;
  }
  return offsets;
}

/** The whole column's height, gaps included. */
export function documentHeight(plans: readonly RenderPlan[], gap: number = PAGE_GAP): number {
  if (plans.length === 0) return 0;
  return plans.reduce((total, plan) => total + plan.cssHeight, 0) + gap * (plans.length - 1);
}

/**
 * Where the reading eye is, as a page number. 1-based, and never outside the document.
 *
 * The measuring point is a third of the way down the viewport rather than its top edge,
 * because a page whose last two lines are still on screen is not the page being read. Without
 * that offset the indicator changes one line before the page does, which reads as a bug in the
 * counter rather than as a scroll position.
 */
export function pageAtOffset(
  plans: readonly RenderPlan[],
  scrollTop: number,
  viewportHeight: number,
  gap: number = PAGE_GAP,
): number {
  if (plans.length === 0) return 1;
  const point = Math.max(0, scrollTop) + Math.max(0, viewportHeight) / 3;

  const offsets = pageOffsets(plans, gap);
  for (let index = plans.length - 1; index >= 0; index -= 1) {
    if (point >= (offsets[index] as number)) return index + 1;
  }
  return 1;
}

/** The pages that hold a painted canvas when `page` is the one being read. */
export function renderWindow(
  page: number,
  pageCount: number,
  radius: number = RENDER_WINDOW_RADIUS,
): { first: number; last: number } {
  if (pageCount <= 0) return { first: 1, last: 0 };
  const centre = Math.min(Math.max(1, Math.round(page)), pageCount);
  return {
    first: Math.max(1, centre - radius),
    last: Math.min(pageCount, centre + radius),
  };
}

/**
 * Where to scroll so an anchored passage is on screen, with room above it.
 *
 * A quarter of the viewport is left above the passage rather than putting it at the very top
 * edge: a highlight flush against the top of the stage reads as the start of the page rather
 * than as the thing that was jumped to, and there is nowhere to see the sentence before it.
 */
export function anchorScrollTop(
  plans: readonly RenderPlan[],
  anchor: HighlightAnchor,
  viewportHeight: number,
  gap: number = PAGE_GAP,
): number | null {
  const plan = plans[anchor.page - 1];
  if (!plan) return null;
  const first = anchor.rects[0];
  if (!first) return null;

  const offsets = pageOffsets(plans, gap);
  const top = (offsets[anchor.page - 1] as number) + first.y * plan.cssHeight;
  return Math.max(0, top - Math.max(0, viewportHeight) / 4);
}

/**
 * Which anchor a click landed on, or `-1`.
 *
 * The highlight layer never takes the pointer — it sits under pdf.js's text layer so a teacher
 * can still select the words a highlight covers — so a click on a highlight is a click on the
 * page that happens to be inside one of its rectangles. Deciding that here keeps it arithmetic:
 * the component contributes the click's position and nothing else.
 *
 * Later anchors win, because a highlight drawn over another is the one a teacher sees.
 */
export function anchorHit(
  anchors: readonly HighlightAnchor[],
  point: { readonly x: number; readonly y: number },
  box: Box,
): number {
  if (!(box.width > 0) || !(box.height > 0)) return -1;
  const x = point.x / box.width;
  const y = point.y / box.height;

  for (let index = anchors.length - 1; index >= 0; index -= 1) {
    const anchor = anchors[index] as HighlightAnchor;
    const hit = anchor.rects.some((rect) =>
      x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h);
    if (hit) return index;
  }
  return -1;
}

// --- a highlight, pinned to the page rather than to the screen -------------------------------

/**
 * A rectangle in page coordinates: `0..1` of the page box, origin top-left, `y` downwards.
 *
 * The units are the whole point. Nothing here remembers a zoom level, a device pixel ratio or
 * a column width, so an anchor captured at 75% on a phone draws in the right place at 300% on
 * a desktop without being recomputed.
 */
export interface AnchorRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** A selection, pinned to the document. The unit pass 2 will store. */
export interface HighlightAnchor {
  /** 1-based, and the number the page indicator shows. */
  readonly page: number;
  /** One rect per line of the selection, in page coordinates. */
  readonly rects: readonly AnchorRect[];
  /** The selected text, whitespace collapsed. What makes an anchor readable in a comment. */
  readonly quote: string;
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * Pixels to fractions, clamped to the page and stripped of what cannot be drawn.
 *
 * `Range.getClientRects()` is generous: it returns zero-width boxes at line ends, exact
 * duplicates where a selection crosses an inline element boundary, and boxes that spill a
 * fraction of a pixel past the page box. All three are dropped here rather than in the
 * renderer, so what is stored is already what should be drawn.
 */
export function normaliseRects(rects: readonly Rect[], box: Box): AnchorRect[] {
  if (!(box.width > 0) || !(box.height > 0)) return [];

  const out: AnchorRect[] = [];
  const seen = new Set<string>();

  for (const rect of rects) {
    if (!(rect.w > 0) || !(rect.h > 0)) continue;

    const x = clamp01(rect.x / box.width);
    const y = clamp01(rect.y / box.height);
    const w = clamp01((rect.x + rect.w) / box.width) - x;
    const h = clamp01((rect.y + rect.h) / box.height) - y;
    if (!(w > 0) || !(h > 0)) continue;

    // Two identical boxes stack into one darker one on screen, which reads as a second
    // highlight that is not there.
    const key = `${x.toFixed(6)}:${y.toFixed(6)}:${w.toFixed(6)}:${h.toFixed(6)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ x, y, w, h });
  }

  return out;
}

/** Fractions back to pixels, for a page box of whatever size is on screen now. */
export function toPixelRects(rects: readonly AnchorRect[], box: Box): Rect[] {
  return rects.map((rect) => ({
    x: rect.x * box.width,
    y: rect.y * box.height,
    w: rect.w * box.width,
    h: rect.h * box.height,
  }));
}

/**
 * A selection as an anchor, or `null` if there is nothing worth anchoring.
 *
 * A selection with no text, or one whose rectangles all collapse to nothing, is a click —
 * not a highlight — and returning an empty anchor for it would put an invisible record on
 * every stray tap once pass 2 starts saving these.
 */
export function anchorFromRects(params: {
  page: number;
  quote: string;
  rects: readonly Rect[];
  box: Box;
}): HighlightAnchor | null {
  const quote = params.quote.replace(/\s+/gu, ' ').trim();
  if (quote.length === 0) return null;
  if (!Number.isInteger(params.page) || params.page < 1) return null;

  const rects = normaliseRects(params.rects, params.box);
  if (rects.length === 0) return null;

  return { page: params.page, rects, quote };
}

/**
 * Whether an unknown value is an anchor this code can draw.
 *
 * It exists now, unused by the viewer, because pass 2 sends these over the wire: the Worker
 * will need exactly this check, and writing it beside the type that defines it is the only
 * way the two stay in step.
 */
export function isHighlightAnchor(value: unknown): value is HighlightAnchor {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;

  if (!Number.isInteger(candidate.page) || (candidate.page as number) < 1) return false;
  if (typeof candidate.quote !== 'string' || candidate.quote.length === 0) return false;
  if (!Array.isArray(candidate.rects) || candidate.rects.length === 0) return false;

  return candidate.rects.every((rect: unknown) => {
    if (typeof rect !== 'object' || rect === null) return false;
    const r = rect as Record<string, unknown>;
    const inPage = (n: unknown): boolean => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 1;
    if (!inPage(r.x) || !inPage(r.y) || !inPage(r.w) || !inPage(r.h)) return false;
    if (!((r.w as number) > 0) || !((r.h as number) > 0)) return false;
    // A rect that runs off the page is a rect measured against a different box.
    return (r.x as number) + (r.w as number) <= 1.000001
      && (r.y as number) + (r.h as number) <= 1.000001;
  });
}

// --- an anchor that is about to be stored ----------------------------------------------------

/**
 * The walls on a stored anchor.
 *
 * `isHighlightAnchor` answers whether a value is an anchor at all; these two answer whether it
 * is one small enough to keep. Without them a single request can store an unbounded blob: the
 * quote is whatever was selected, and `getClientRects` returns one rectangle per *line*, so a
 * selection of a whole document is thousands of them. Both are generous — a thousand
 * characters is several paragraphs, and two hundred rectangles is a page and a half of prose —
 * and both are ceilings rather than sizes anything is expected to reach.
 */
export const MAX_ANCHOR_QUOTE = 1000;
export const MAX_ANCHOR_RECTS = 200;

/**
 * An anchor cut down to what may be stored.
 *
 * The browser calls this before posting so a teacher who selects six paragraphs gets a comment
 * rather than a refusal; the Worker refuses anything past the same ceilings, because a client
 * that forgot to call this is a client, and the wall is the server's.
 */
export function clampAnchor(anchor: HighlightAnchor): HighlightAnchor {
  const quote = anchor.quote.length > MAX_ANCHOR_QUOTE
    ? `${anchor.quote.slice(0, MAX_ANCHOR_QUOTE - 1)}…`
    : anchor.quote;
  const rects = anchor.rects.length > MAX_ANCHOR_RECTS
    ? anchor.rects.slice(0, MAX_ANCHOR_RECTS)
    : anchor.rects;
  return { page: anchor.page, rects, quote };
}

/**
 * Whether a stored anchor can be drawn on the document that is actually open.
 *
 * Three answers rather than two, and the third is the one that matters. A comment whose anchor
 * cannot be placed — a page past the end of the document, or rectangles that no longer describe
 * a page — must still render, showing the passage it quotes and saying plainly that it is no
 * longer attached. What it must never do is point confidently at the wrong place, and
 * `unknown` is how "we have not read the document yet" is kept distinct from "it is not there":
 * treating the first as the second labels every comment detached for as long as a PDF takes to
 * open.
 */
export type AnchorPlacement = 'placed' | 'unplaceable' | 'unknown';

export function anchorPlacement(anchor: unknown, pageCount: number | null): AnchorPlacement {
  if (!isHighlightAnchor(anchor)) return 'unplaceable';
  if (pageCount === null || !(pageCount > 0)) return 'unknown';
  return anchor.page <= pageCount ? 'placed' : 'unplaceable';
}

// --- a picture of the passage ----------------------------------------------------------------

/**
 * How a highlighted passage is painted on its own, as a picture beside its comment.
 *
 * The quote is whatever the browser's selection said the words were, and for some PDFs that is
 * nonsense: a font embedded without a map from its glyphs back to letters gives pdf.js glyph
 * numbers rather than characters, and "How many walnuts" is stored as "+RZ PDQ| ZDOQXWV". The
 * rectangles are still exactly right — they came from where the glyphs were drawn — so the
 * page's own pixels under them are the one rendering of the passage that cannot be wrong.
 *
 * This is the arithmetic for painting only that part of the page: the passage's bounding box,
 * a little margin so a line is not cut at its descenders, and a scale that makes the crop
 * `targetWidth` device pixels wide unless the canvas caps say otherwise. pdf.js is then asked
 * for the page *offset* so the crop lands at the canvas's origin, which means a one-line
 * passage costs a one-line canvas rather than a whole page rendered and thrown away.
 */
export interface SnapshotPlan {
  readonly scale: number;
  /** Handed to `getViewport` so the crop's corner is the canvas's corner. */
  readonly offsetX: number;
  readonly offsetY: number;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** The highlight, in the snapshot canvas's own pixels, so it can be tinted as on the page. */
  readonly rects: readonly Rect[];
}

/** Margin around a passage, in PDF points. Enough for descenders and a little air. */
export const SNAPSHOT_MARGIN = 6;
const SNAPSHOT_MIN_SCALE = 0.5;
const SNAPSHOT_MAX_SCALE = 4;

export function planAnchorSnapshot(
  anchor: HighlightAnchor,
  page: PageSize,
  targetWidth: number,
): SnapshotPlan | null {
  if (!(page.width > 0) || !(page.height > 0) || !(targetWidth > 0)) return null;
  if (anchor.rects.length === 0) return null;

  let left = 1;
  let top = 1;
  let right = 0;
  let bottom = 0;
  for (const rect of anchor.rects) {
    left = Math.min(left, rect.x);
    top = Math.min(top, rect.y);
    right = Math.max(right, rect.x + rect.w);
    bottom = Math.max(bottom, rect.y + rect.h);
  }

  // Into points, padded, and kept on the page.
  const x0 = Math.max(0, left * page.width - SNAPSHOT_MARGIN);
  const y0 = Math.max(0, top * page.height - SNAPSHOT_MARGIN);
  const x1 = Math.min(page.width, right * page.width + SNAPSHOT_MARGIN);
  const y1 = Math.min(page.height, bottom * page.height + SNAPSHOT_MARGIN);
  const w = x1 - x0;
  const h = y1 - y0;
  if (!(w > 0) || !(h > 0)) return null;

  // The same walls a page canvas has. A passage the height of a page is a page.
  const scale = Math.min(
    SNAPSHOT_MAX_SCALE,
    Math.max(SNAPSHOT_MIN_SCALE, targetWidth / w),
    MAX_CANVAS_DIMENSION / w,
    MAX_CANVAS_DIMENSION / h,
    Math.sqrt(MAX_CANVAS_PIXELS / (w * h)),
  );

  return {
    scale,
    offsetX: -x0 * scale,
    offsetY: -y0 * scale,
    canvasWidth: Math.max(1, Math.floor(w * scale)),
    canvasHeight: Math.max(1, Math.floor(h * scale)),
    rects: anchor.rects.map((rect) => ({
      x: (rect.x * page.width - x0) * scale,
      y: (rect.y * page.height - y0) * scale,
      w: rect.w * page.width * scale,
      h: rect.h * page.height * scale,
    })),
  };
}
